import { CheckoutData, CheckoutResult, PaymentDetails } from "@lemmonhq/types";
import { getAPIClient } from "../helpers";

export type TerminalPaymentWorkflowStatus = {
  status: string;
  output?: {
    status: string;
    paymentId?: number;
    transactionId?: number;
    error?: string;
    checkoutResult?: any;
    tipAmount?: number;
  };
  error?: string;
};

export const terminalTransactionsAPI = {
  createPaymentTx: async (
    amount: number,
    checkout: CheckoutData,
    checkoutId: string,
    paymentMethod: "twint" | "card",
    signal?: AbortSignal,
  ) => {
    const client = getAPIClient();
    const resp = await client.api.v2.payments.transactions.$post(
      {
        json: {
          amount,
          checkout,
          paymentMethod,
          checkoutId,
        },
      },
      {
        init: { signal },
      },
    );
    if (resp.ok) {
      return await resp.json();
    } else {
      let message = "Unable to Initiate";
      try {
        const json: any = await resp.json();
        message = json.message || json.error;
      } catch (e) {
        //
      }
      throw new Error(message);
    }
  },
  processPaymentTx: async (paymentId: number, signal?: AbortSignal) => {
    const client = getAPIClient();
    const resp = await client.api.v2.payments.transactions[
      ":payment_id"
    ].process.$post(
      {
        param: { payment_id: paymentId.toString() },
      },
      {
        init: { signal },
      },
    );
    if (resp.ok) {
      const json = await resp.json();
      return json;
    } else {
      throw new Error("unable to fetch status of checkout");
    }
  },
  checkTerminalPaymentResult: async (
    paymentId: number,
    signal?: AbortSignal,
  ) => {
    const client = getAPIClient();
    const resp = await client.api.v2.payments.transactions[":payment_id"].$get(
      {
        param: { payment_id: paymentId.toString() },
      },
      {
        init: { signal },
      },
    );
    if (resp.ok) {
      return resp.json();
    } else {
      throw new Error("Failed to poll Terminal transaction");
    }
  },
  getPaymentTxResult: async (
    paymentId: number,
  ): Promise<{ payment: PaymentDetails; result: CheckoutResult }> => {
    const client = getAPIClient();
    const resp = await client.api.v2.payments.transactions.result[
      ":payment_id"
    ].$get({
      param: { payment_id: paymentId.toString() },
    });
    if (resp.ok) {
      const json = await resp.json();
      // todo: remove this typecast
      return json as { payment: PaymentDetails; result: CheckoutResult };
    } else {
      throw new Error("unable to fetch status of checkout");
    }
  },
  // Workflow-based API methods
  getTerminalPaymentByCheckoutId: async (
    checkoutId: string,
  ): Promise<{
    paymentId?: number;
    transactionId?: number | string | null;
    status?: string;
    message?: string;
  }> => {
    const client = getAPIClient();
    const resp = await client.api.v2.payments["terminal-workflow"].checkout[
      ":checkoutId"
    ].payment.$get({
      param: { checkoutId },
    });
    if (resp.ok) {
      return await resp.json();
    } else {
      throw new Error("Failed to get payment for checkout");
    }
  },

  // Restate-based API methods (calls server-side routes)
  invokeTerminalPaymentService: async (
    amount: number,
    _checkoutId: string,
    checkout: CheckoutData,
    paymentMethod: "card" | "twint",
  ): Promise<{ invocationId: string; checkoutId: string }> => {
    const client = getAPIClient();
    const checkoutId = crypto.randomUUID();
    const resp = await client.api.v2.payments.transactions[
      "restate-workflow"
    ].$post({
      json: {
        amount,
        checkout: { ...checkout, id: checkoutId },
        checkoutId,
        paymentMethod,
      },
    });

    if (resp.ok) {
      return await resp.json();
    } else {
      let message;
      if (resp.status === 422) {
        const json: any = await resp.json();
        message = json.message || json.error;
      }
      throw new Error(message || "Failed to invoke Restate service");
    }
  },

  getTerminalPaymentServiceStatus: async (
    workflowId: string,
  ): Promise<{
    status: "pending" | "completed" | "error";
    output?: any;
    error?: string;
  }> => {
    const client = getAPIClient();
    const resp = await client.api.v2.payments.transactions["restate-workflow"][
      ":invocationId"
    ].$get({
      param: { invocationId: workflowId },
    });

    if (resp.ok) {
      const result = await resp.json();
      return result as {
        status: "pending" | "completed" | "error";
        output?: any;
        error?: string;
      };
    } else {
      throw new Error("Failed to get Restate service status");
    }
  },
};
